import { useState, useEffect } from "react";
import { Target, Clock, Award, TrendingUp } from "lucide-react";
import { useT } from "@/shared/i18n/useT";
import { supabase } from "@/shared/lib/supabase";

const WEEKLY_SESSION_GOAL = 5;

interface DashboardStats {
  totalSessions: number;
  weekMinutes: number;
  avgScore: number | null;
  weeklyProgress: number; // 0–100
}

function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

async function fetchStats(): Promise<DashboardStats> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token)
    return {
      totalSessions: 0,
      weekMinutes: 0,
      avgScore: null,
      weeklyProgress: 0,
    };

  const response = await fetch("/api/sessions?status=completed", {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok)
    return {
      totalSessions: 0,
      weekMinutes: 0,
      avgScore: null,
      weeklyProgress: 0,
    };

  const data = await response.json();
  const sessions: any[] = data.sessions ?? [];

  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setHours(0, 0, 0, 0);
  startOfWeek.setDate(now.getDate() - now.getDay()); // Sunday

  let weekMinutes = 0;
  let weekCount = 0;
  let scoreSum = 0;
  let scoreCount = 0;

  for (const s of sessions) {
    const completedAt = s.completed_at ? new Date(s.completed_at) : null;
    const createdAt = new Date(s.created_at);

    // Session duration
    if (completedAt) {
      const durationMs = completedAt.getTime() - createdAt.getTime();
      const durationMin = Math.max(0, Math.round(durationMs / 60000));

      if (completedAt >= startOfWeek) {
        weekMinutes += durationMin;
        weekCount++;
      }
    }

    // Average score
    const score = s.analysis?.overallScore;
    if (typeof score === "number" && score !== null && score !== undefined) {
      scoreSum += score;
      scoreCount++;
    }
  }

  return {
    totalSessions: sessions.length,
    weekMinutes,
    avgScore: scoreCount > 0 ? Math.round(scoreSum / scoreCount) : null,
    weeklyProgress: Math.min(
      100,
      Math.round((weekCount / WEEKLY_SESSION_GOAL) * 100),
    ),
  };
}

export function ProgressCard({ refreshKey }: { refreshKey?: number } = {}) {
  const t = useT();
  const [stats, setStats] = useState<DashboardStats | null>(null);

  useEffect(() => {
    fetchStats()
      .then(setStats)
      .catch(() => {});
  }, [refreshKey]);

  const loading = stats === null;

  const items = [
    {
      icon: Target,
      label: t("ui.progressCard.sessionsLabel"),
      value: loading ? "…" : String(stats.totalSessions),
      subtitle: t("ui.progressCard.totalCompleted"),
    },
    {
      icon: Clock,
      label: t("ui.progressCard.practiceLabel"),
      value: loading ? "…" : formatMinutes(stats.weekMinutes),
      subtitle: t("ui.progressCard.thisWeek"),
    },
    {
      icon: Award,
      label: t("ui.progressCard.avgScoreLabel"),
      value: loading
        ? "…"
        : stats.avgScore !== null
          ? `${stats.avgScore}%`
          : "—",
      subtitle: t("ui.progressCard.acrossAllSessions"),
    },
    {
      icon: TrendingUp,
      label: t("ui.progressCard.progressLabel"),
      value: loading ? "…" : `${stats.weeklyProgress}%`,
      subtitle: t("ui.progressCard.towardWeeklyGoal", {
        goal: WEEKLY_SESSION_GOAL,
      }),
    },
  ];

  return (
    <>
      {items.map((stat, index) => {
        const Icon = stat.icon;
        const color = "#244945";
        return (
          <div
            key={index}
            className="glossy-card glossy-card-hover rounded-xl p-5 animate-fade-in-up overflow-hidden relative group"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            {/* Glossy shine overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

            <div className="relative z-10 flex items-center justify-center gap-4">
              <div className="flex items-start justify-between mb-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center shadow-sm relative overflow-hidden group-hover:scale-110 transition-transform duration-300"
                  style={{
                    background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                  }}
                >
                  <div className="absolute inset-0 shimmer opacity-0 group-hover:opacity-100" />
                  <Icon
                    size={20}
                    className="text-white relative z-10"
                    strokeWidth={2}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <span className="text-sm font-medium text-gray-600">
                  {stat.label}
                </span>
                <div
                  className={`text-xl sm:text-2xl font-semibold text-gray-900 ${loading ? "animate-pulse" : ""}`}
                >
                  {stat.value}
                </div>
                <div className="text-xs text-gray-500">{stat.subtitle}</div>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}
